import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, set, remove, push } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  ClockIcon, CalendarIcon, BoltIcon, CheckCircleIcon,
  XMarkIcon, MagnifyingGlassIcon, SparklesIcon,
  ArrowPathIcon, QrCodeIcon, CreditCardIcon,
  UserIcon, ChevronDownIcon, CheckIcon,
  PlusIcon, MinusIcon, FunnelIcon,
} from '@heroicons/react/24/outline';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PRIMARY = '#800020';
const DARK    = '#1a1a2e';

const SWAL_Z = { customClass: { container: 'vis-swal-z' } };
if (typeof document !== 'undefined' && !document.getElementById('vis-swal-z-style')) {
  const s = document.createElement('style');
  s.id = 'vis-swal-z-style';
  s.textContent = '.vis-swal-z{z-index:99999!important}';
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, ...SWAL_Z });

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtDateTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};
const fmtTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
};
const fmtDuration = (mins) => {
  if (!mins && mins !== 0) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + 'm' : ''}`.trim() : `${m}m`;
};

const DAYS_OF_WEEK = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const todayDay = () => new Date().toLocaleDateString('en-KE', { weekday: 'long' }).toLowerCase();
const todayStr = () => new Date().toISOString().slice(0, 10);

const isScheduleToday = (sch) => {
  if (sch.active === false) return false;
  if (sch.type === 'recurring')      return (sch.days || []).includes(todayDay());
  if (sch.type === 'specific_date')  return sch.date === todayStr();
  if (sch.type === 'specific_dates') return (sch.dates || []).includes(todayStr());
  return false;
};

const fmtScheduleTime = (sch) => {
  const t = sch.time || '';
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h);
  return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const Visits = () => {
  const { userData, currentUser } = useAuth();

  const [activeTab,       setActiveTab]       = useState('member');
  const [activities,      setActivities]      = useState([]);
  const [memberVisits,    setMemberVisits]     = useState({}); // { activityId_scheduleIdx: visitRecord }
  const [guestVisits,     setGuestVisits]      = useState([]);
  const [loading,         setLoading]          = useState(true);
  const [saving,          setSaving]           = useState(null); // activityId_schedIdx being toggled
  const [activitySearch,  setActivitySearch]   = useState('');
  const [guestSearch,     setGuestSearch]      = useState('');
  const [filterGuestStatus, setFilterGuestStatus] = useState('all');
  const [checkInModal,    setCheckInModal]     = useState(null); // activity object for detail modal

  const membership = userData?.membership || {};
  const isActive   = membership.status === 'active' && membership.expiryDate > Date.now();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [actSnap, memberVisitsSnap, guestSnap] = await Promise.all([
        get(ref(database, 'activities')),
        get(ref(database, `users/${currentUser.uid}/memberVisits`)),
        get(ref(database, `users/${currentUser.uid}/guestVisits`)),
      ]);

      setActivities(actSnap.exists()
        ? Object.entries(actSnap.val()).map(([k, v]) => ({ id: k, ...v })).filter(a => a.active !== false)
        : []);

      setMemberVisits(memberVisitsSnap.exists() ? memberVisitsSnap.val() : {});

      setGuestVisits(guestSnap.exists()
        ? Object.entries(guestSnap.val())
            .map(([k, v]) => ({ id: k, ...v }))
            .sort((a, b) => b.checkedIn - a.checkedIn)
        : []);
    } catch (err) {
      console.error(err);
      toast('error', 'Failed to load visits');
    } finally {
      setLoading(false);
    }
  }, [currentUser.uid]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Check-in key ─────────────────────────────────────────────────────────
  const visitKey = (activityId, schedIdx) => `${activityId}_${schedIdx}`;

  const isCheckedIn = (activityId, schedIdx) =>
    !!memberVisits[visitKey(activityId, schedIdx)];

  // ── Toggle check-in ───────────────────────────────────────────────────────
  const toggleCheckIn = async (activity, schedIdx, schedule) => {
    if (!isActive) {
      return toast('error', 'No active membership', 'Your membership must be active to log visits');
    }

    const key   = visitKey(activity.id, schedIdx);
    const isIn  = isCheckedIn(activity.id, schedIdx);
    const vRef  = ref(database, `users/${currentUser.uid}/memberVisits/${key}`);

    setSaving(key);
    try {
      if (isIn) {
        // Unmark visit
        await remove(vRef);
        setMemberVisits(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        toast('success', 'Visit unmarked');
      } else {
        // Mark as visited
        const record = {
          activityId:    activity.id,
          activityTitle: activity.title,
          activityName:  activity.name,
          scheduleIdx:   schedIdx,
          scheduleLabel: schedule?.label || `Schedule ${schedIdx + 1}`,
          scheduleTime:  fmtScheduleTime(schedule),
          visitedAt:     Date.now(),
          date:          todayStr(),
          membershipStatus: membership.status,
        };
        await set(vRef, record);
        setMemberVisits(prev => ({ ...prev, [key]: record }));
        toast('success', '✅ Visit logged!', `${activity.title} — ${schedule?.label || fmtScheduleTime(schedule)}`);
      }
    } catch (err) {
      console.error(err);
      toast('error', 'Failed to update visit');
    } finally {
      setSaving(null);
    }
  };

  // ── Filtered activities for member tab ───────────────────────────────────
  const filteredActivities = activities.filter(act => {
    if (!activitySearch) return true;
    const q = activitySearch.toLowerCase();
    return act.title?.toLowerCase().includes(q) || act.name?.toLowerCase().includes(q);
  });

  // ── Filtered guest visits ─────────────────────────────────────────────────
  const filteredGuest = guestVisits.filter(v => {
    if (filterGuestStatus !== 'all' && v.status !== filterGuestStatus) return false;
    if (guestSearch) {
      const q = guestSearch.toLowerCase();
      return v.activityTitle?.toLowerCase().includes(q) || v.cardName?.toLowerCase().includes(q);
    }
    return true;
  });

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalMemberVisits = Object.keys(memberVisits).length;
  const todayMemberVisits = Object.values(memberVisits).filter(v => v.date === todayStr()).length;
  const totalGuestVisits  = guestVisits.length;
  const totalSpent        = guestVisits.reduce((sum, v) => sum + (v.amountPaid || v.totalPrice || 0), 0);

  const TABS = [
    { key: 'member', label: 'My Activity Visits', icon: <SparklesIcon className="h-4 w-4" /> },
    { key: 'guest',  label: 'Guest Visit History', icon: <QrCodeIcon   className="h-4 w-4" /> },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#f0f0f0 100%)' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

        {/* ── HEADER ── */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-gray-900">My Visits</h1>
              <p className="text-gray-500 text-sm mt-1">Track your activity attendance and guest visit history</p>
            </div>
            <button onClick={fetchAll}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow-sm transition-all">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </motion.div>

        {/* ── MEMBERSHIP NOTICE ── */}
        {!isActive && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="mb-6 p-4 rounded-2xl border-2 flex items-start gap-3"
            style={{ borderColor: '#fcd5de', background: '#fff5f7' }}>
            <SparklesIcon className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: PRIMARY }} />
            <div>
              <p className="font-bold text-gray-900">Membership not active</p>
              <p className="text-sm text-gray-500 mt-0.5">
                You need an active membership to log activity visits. Guest visit history is still visible below.
              </p>
            </div>
          </motion.div>
        )}

        {/* ── STAT PILLS ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Activity Visits', val: totalMemberVisits, color: PRIMARY,   icon: '🏋️' },
            { label: 'Today\'s Visits',       val: todayMemberVisits, color: '#16a34a', icon: '📅' },
            { label: 'Guest Visits',          val: totalGuestVisits,  color: '#2563eb', icon: '🪪'  },
            { label: 'Total Guest Spent',     val: `KES ${totalSpent.toLocaleString()}`, color: '#d97706', icon: '💰' },
          ].map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <p className="text-xl mb-1">{s.icon}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="font-black text-lg mt-0.5" style={{ color: s.color }}>{s.val}</p>
            </motion.div>
          ))}
        </div>

        {/* ── TABS ── */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border border-gray-100 mb-6 w-fit">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all"
              style={{
                background: activeTab === tab.key ? PRIMARY : 'transparent',
                color:      activeTab === tab.key ? '#fff'  : '#6b7280',
              }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
            <p className="text-gray-500 mt-3 text-sm">Loading visits…</p>
          </div>
        ) : (
          <>
            {/* ══════════════════════════════════════════════════
                MEMBER VISITS TAB
            ══════════════════════════════════════════════════ */}
            {activeTab === 'member' && (
              <div>
                {/* Search */}
                <div className="relative mb-5 max-w-sm">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input value={activitySearch} onChange={e => setActivitySearch(e.target.value)}
                    placeholder="Search activities…"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 bg-white shadow-sm"
                    style={{ '--tw-ring-color': PRIMARY }} />
                </div>

                {filteredActivities.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <SparklesIcon className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No activities found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredActivities.map((activity, ai) => {
                      const activeSchedules = (activity.schedules || []).map((s, i) => ({ ...s, idx: i }));
                      const checkedCount = activeSchedules.filter(s => isCheckedIn(activity.id, s.idx)).length;

                      return (
                        <motion.div key={activity.id}
                          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: ai * 0.04 }}
                          className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all">

                          {/* Activity header */}
                          <div className="flex items-center gap-4 p-5 cursor-pointer"
                            onClick={() => setCheckInModal(checkInModal?.id === activity.id ? null : activity)}>
                            {activity.photos?.[0] ? (
                              <img src={activity.photos[0]} alt={activity.title}
                                className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{ background: `${PRIMARY}15` }}>
                                <BoltIcon className="h-7 w-7" style={{ color: PRIMARY }} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-black text-gray-900 text-base">{activity.title}</h3>
                              {activity.trainers?.length > 0 && (
                                <p className="text-xs text-gray-400 mt-0.5">{activity.trainers.join(' · ')}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                                  style={{ background: `${PRIMARY}10`, color: PRIMARY }}>
                                  {activeSchedules.length} schedule{activeSchedules.length !== 1 ? 's' : ''}
                                </span>
                                {checkedCount > 0 && (
                                  <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-green-100 text-green-700">
                                    ✓ {checkedCount} visited
                                  </span>
                                )}
                                {activity.pricePerVisit > 0 && (
                                  <span className="text-xs text-gray-400">
                                    KES {Number(activity.pricePerVisit).toLocaleString()} / visit
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0 transition-transform duration-200"
                              style={{ transform: checkInModal?.id === activity.id ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                            </div>
                          </div>

                          {/* Schedule check-ins */}
                          <AnimatePresence>
                            {checkInModal?.id === activity.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="overflow-hidden border-t border-gray-100">
                                <div className="p-5 pt-4 space-y-3"
                                  style={{ background: '#fafafa' }}>
                                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                                    Mark your attendance for each schedule
                                  </p>
                                  {activeSchedules.length === 0 ? (
                                    <p className="text-sm text-gray-400 italic">No schedules defined for this activity</p>
                                  ) : (
                                    activeSchedules.map(sch => {
                                      const checked    = isCheckedIn(activity.id, sch.idx);
                                      const key        = visitKey(activity.id, sch.idx);
                                      const isSavingThis = saving === key;
                                      const isToday    = isScheduleToday(sch);
                                      const visitRec   = memberVisits[key];

                                      return (
                                        <div key={sch.idx}
                                          className="flex items-center gap-4 p-4 rounded-xl border-2 transition-all"
                                          style={{
                                            borderColor: checked ? '#22c55e' : '#e5e7eb',
                                            background:  checked ? '#f0fdf4' : '#fff',
                                          }}>
                                          {/* Schedule info */}
                                          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ background: isToday ? `${PRIMARY}15` : '#f3f4f6' }}>
                                            {sch.type === 'recurring'
                                              ? <BoltIcon     className="h-5 w-5" style={{ color: isToday ? PRIMARY : '#9ca3af' }} />
                                              : <CalendarIcon className="h-5 w-5" style={{ color: isToday ? PRIMARY : '#9ca3af' }} />
                                            }
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            {sch.label && <p className="font-bold text-gray-800 text-sm">{sch.label}</p>}
                                            <p className="text-xs text-gray-500">
                                              {sch.type === 'recurring'
                                                ? `${(sch.days || []).map(d => d.slice(0,3)).join(', ')} · ${fmtScheduleTime(sch)}`
                                                : sch.type === 'specific_date'
                                                  ? `${sch.date} · ${fmtScheduleTime(sch)}`
                                                  : `${(sch.dates || []).length} dates · ${fmtScheduleTime(sch)}`
                                              }
                                            </p>
                                            {isToday && (
                                              <span className="text-xs font-bold text-green-600">📅 Scheduled today</span>
                                            )}
                                            {checked && visitRec && (
                                              <p className="text-xs text-green-600 mt-0.5">
                                                ✓ Visited {fmtDateTime(visitRec.visitedAt)}
                                              </p>
                                            )}
                                          </div>

                                          {/* Toggle button */}
                                          <button
                                            onClick={() => toggleCheckIn(activity, sch.idx, sch)}
                                            disabled={isSavingThis || !isActive}
                                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50 flex-shrink-0"
                                            style={checked ? {
                                              background: '#fef2f2',
                                              color: '#dc2626',
                                              border: '2px solid #fcd5de',
                                            } : {
                                              background: `linear-gradient(135deg,${PRIMARY},#b30000)`,
                                              color: '#fff',
                                              boxShadow: `0 4px 14px ${PRIMARY}30`,
                                            }}>
                                            {isSavingThis ? (
                                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                            ) : checked ? (
                                              <><XMarkIcon className="h-4 w-4" /> Unmark</>
                                            ) : (
                                              <><CheckIcon className="h-4 w-4" /> Mark Visited</>
                                            )}
                                          </button>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                )}

                {/* My visited log */}
                {Object.keys(memberVisits).length > 0 && (
                  <div className="mt-8">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Visit Log</h2>
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
                              {['Activity', 'Schedule', 'Visited At', 'Date'].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {Object.entries(memberVisits)
                              .map(([k, v]) => ({ key: k, ...v }))
                              .sort((a, b) => b.visitedAt - a.visitedAt)
                              .map(v => (
                                <tr key={v.key} className="hover:bg-gray-50 transition-colors">
                                  <td className="px-4 py-3 font-semibold text-gray-900">{v.activityTitle}</td>
                                  <td className="px-4 py-3 text-gray-600 text-xs">{v.scheduleLabel} · {v.scheduleTime}</td>
                                  <td className="px-4 py-3 text-xs text-gray-500">{fmtDateTime(v.visitedAt)}</td>
                                  <td className="px-4 py-3 text-xs text-gray-500">{v.date}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ══════════════════════════════════════════════════
                GUEST VISITS TAB
            ══════════════════════════════════════════════════ */}
            {activeTab === 'guest' && (
              <div>
                {/* Filters */}
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <div className="relative flex-1" style={{ minWidth: 200 }}>
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input value={guestSearch} onChange={e => setGuestSearch(e.target.value)}
                      placeholder="Search by activity or card…"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 bg-white shadow-sm"
                      style={{ '--tw-ring-color': PRIMARY }} />
                  </div>
                  <select value={filterGuestStatus} onChange={e => setFilterGuestStatus(e.target.value)}
                    className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:outline-none">
                    <option value="all">All statuses</option>
                    <option value="completed">Completed</option>
                    <option value="ongoing">Ongoing</option>
                  </select>
                </div>

                {filteredGuest.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <QrCodeIcon className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No guest visits found</p>
                    <p className="text-gray-400 text-sm mt-1">Guest visits are recorded when you pay for individual activity access at reception</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredGuest.map((visit, i) => (
                      <motion.div key={visit.id}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all">
                        <div className="flex items-start gap-4 p-5">
                          {/* Status dot */}
                          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                            style={{
                              background: visit.status === 'completed' ? '#f0fdf4' : visit.status === 'ongoing' ? '#eff6ff' : '#f9fafb',
                            }}>
                            {visit.status === 'completed'
                              ? <CheckCircleIcon className="h-6 w-6 text-green-600" />
                              : visit.status === 'ongoing'
                                ? <ClockIcon className="h-6 w-6 text-blue-600" />
                                : <XMarkIcon className="h-6 w-6 text-gray-400" />
                            }
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-bold text-gray-900">{visit.activityTitle || '—'}</p>
                                {visit.activities?.length > 1 && (
                                  <p className="text-xs text-gray-400">{visit.activities.length} activities</p>
                                )}
                                {visit.scheduleLabels?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {visit.scheduleLabels.map((s, si) => (
                                      <span key={si} className="text-xs px-1.5 py-0.5 rounded font-medium"
                                        style={{ background: `${PRIMARY}10`, color: PRIMARY }}>{s}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${
                                visit.status === 'completed' ? 'bg-green-100 text-green-700' :
                                visit.status === 'ongoing'   ? 'bg-blue-100 text-blue-700'   :
                                'bg-gray-100 text-gray-500'
                              }`}>
                                {visit.status}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                              <div>
                                <p className="text-xs text-gray-400">Check In</p>
                                <p className="text-xs font-semibold text-gray-700">{fmtDateTime(visit.checkedIn)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400">Duration</p>
                                <p className="text-xs font-semibold text-gray-700">{fmtDuration(visit.actualDurationMinutes)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400">Card</p>
                                <p className="text-xs font-semibold text-gray-700 font-mono">{visit.cardName || visit.cardUid || '—'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-400">Paid</p>
                                <p className="text-xs font-bold" style={{ color: PRIMARY }}>
                                  KES {(visit.amountPaid || visit.totalPrice || 0).toLocaleString()}
                                  <span className="text-gray-400 font-normal ml-1">({visit.paymentMethod})</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Visits;